import debounce from 'debounce';
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';

import { Icon, IconClose, IconMagnifier } from '@flixbus/honeycomb-icons-react';
import { Button, Input } from '@flixbus/honeycomb-react';

import styles from './styles.module.css';

/**
 * Controlled text search input with a 300 ms debounced `onChange` and an inline clear button.
 *
 * @param {{
 *   value?: string,
 *   onChange?: (value: string) => void,
 *   onReset?: () => void,
 *   label?: string,
 *   placeholder?: string,
 *   withIcon?: boolean,
 * }} props
 */
const AbstractTextSearch = ({
	value = '',
	label = 'Search crew member',
	placeholder = 'Start typing (name or employee number)',
	withIcon = true,
	onChange = () => {},
	onReset = () => {},
	...inputProps
}) => {
	const [localValue, setLocalValue] = useState(value);
	const id = useId();

	useEffect(() => {
		setLocalValue(value);
	}, [value]);

	const debouncedChange = useMemo(
		() =>
			debounce((nextValue) => {
				onChange(nextValue);
			}, 300),
		[onChange]
	);

	useEffect(() => {
		return () => {
			debouncedChange.clear?.();
		};
	}, [debouncedChange]);

	const isDirty = Boolean(localValue);

	const handleInputChange = useCallback(
		(event) => {
			const nextValue = event.target.value;
			setLocalValue(nextValue);
			debouncedChange(nextValue);
		},
		[debouncedChange]
	);

	const handleReset = useCallback(() => {
		setLocalValue('');
		debouncedChange.clear?.();
		onReset();
	}, [debouncedChange, onReset]);

	return (
		<Input
			extraClasses={styles.full}
			{...inputProps}
			id={id}
			label={label}
			placeholder={placeholder}
			value={localValue}
			onChange={handleInputChange}
			{...(withIcon && {
				iconRight: isDirty ? (
					<Button type="button" onClick={handleReset}>
						<Icon extraClasses={styles.alignIcon} InlineIcon={IconClose} />
					</Button>
				) : (
					<Icon InlineIcon={IconMagnifier} />
				),
			})}
		/>
	);
};

export default AbstractTextSearch;
